import { Component } from '@theme/component';

/**
 * Üzletkereső komponens: a szerveroldalon renderelt listából építi fel a Leaflet térképet.
 * Az üzletek adatainak egyetlen forrása a lista (data-* attribútumok), így a térkép és a lista
 * mindig ugyanazt mutatja, és JS nélkül is használható marad az oldal.
 */

/** @type {Promise<any> | null} */
let leafletPromise = null;

/**
 * Betölti a Leaflet stílusát és szkriptjét, oldalanként egyszer.
 *
 * @param {string} scriptUrl
 * @param {string} styleUrl
 * @returns {Promise<any>} A globális Leaflet objektum (window.L).
 */
function loadLeaflet(scriptUrl, styleUrl) {
  const globalWindow = /** @type {any} */ (window);

  if (globalWindow.L) return Promise.resolve(globalWindow.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (styleUrl && !document.querySelector('link[data-leaflet-style]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = styleUrl;
      link.setAttribute('data-leaflet-style', '');
      document.head.append(link);
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.addEventListener('load', () => {
      if (globalWindow.L) {
        resolve(globalWindow.L);
      } else {
        leafletPromise = null;
        reject(new Error('Leaflet loaded but window.L is missing'));
      }
    });
    script.addEventListener('error', () => {
      leafletPromise = null;
      reject(new Error(`Leaflet failed to load: ${scriptUrl}`));
    });
    document.head.append(script);
  });

  return leafletPromise;
}

/**
 * Ékezet- és kisbetű-független kereséshez normalizál (pl. „Győr” → „gyor”).
 *
 * @param {string} value
 * @returns {string}
 */
function normalize(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @typedef {object} StoreLocatorRefs
 * @property {HTMLElement} map
 * @property {HTMLUListElement} list
 * @property {HTMLElement[]} [stores]
 * @property {HTMLInputElement} [search]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [empty]
 */

/**
 * @typedef {object} SelectOptions
 * @property {'list' | 'map' | 'editor'} [source] Honnan érkezett a kiválasztás.
 */

/** @extends {Component<StoreLocatorRefs>} */
class StoreLocatorComponent extends Component {
  requiredRefs = ['map', 'list'];

  /** @type {any} */
  #L = null;

  /** @type {any} */
  #map = null;

  /** @type {any} */
  #markerLayer = null;

  /** @type {Map<string, any>} */
  #markers = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #fitTimer;

  /** @type {{ id: string, options: SelectOptions } | null} */
  #pendingSelection = null;

  #initStarted = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Theme editor: blokk kijelölésekor a térkép az adott üzletre ugrik.
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#startMap();
      },
      { rootMargin: '300px 0px' }
    );
    this.#intersectionObserver.observe(this.refs.map);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    clearTimeout(this.#fitTimer);

    this.#map?.remove();
    this.#map = null;
    this.#markerLayer = null;
    this.#markers.clear();
    this.#pendingSelection = null;
    this.#initStarted = false;
  }

  /**
   * Lista gomb: az adott üzlet megjelenítése a térképen.
   *
   * @param {Event} event
   */
  handleShowOnMap(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const id = target.closest('[data-store-id]')?.getAttribute('data-store-id');
    if (id) this.selectStore(id, { source: 'list' });
  }

  /**
   * Kereső mező: szűri a listát és a jelölőket.
   */
  handleSearch() {
    const query = normalize(this.refs.search?.value ?? '');
    let visibleCount = 0;

    for (const item of this.refs.stores ?? []) {
      const isMatch = !query || normalize(item.dataset.search ?? '').includes(query);
      item.hidden = !isMatch;
      if (isMatch) visibleCount += 1;

      const marker = this.#markers.get(item.dataset.storeId ?? '');
      if (marker && this.#markerLayer) {
        if (isMatch) {
          this.#markerLayer.addLayer(marker);
        } else {
          this.#markerLayer.removeLayer(marker);
        }
      }
    }

    if (this.refs.empty) this.refs.empty.hidden = visibleCount > 0;
    this.#updateStatus(visibleCount);

    clearTimeout(this.#fitTimer);
    this.#fitTimer = setTimeout(() => this.#fitToVisible(true), 250);
  }

  /**
   * Kiemel egy üzletet a listában és a térképen.
   *
   * @param {string} id Blokk-azonosító.
   * @param {SelectOptions} [options]
   */
  selectStore(id, options = {}) {
    const { source = 'list' } = options;
    const item = this.#getItem(id);
    if (!item) return;

    this.#setActive(id);

    if (!this.#map) {
      this.#pendingSelection = { id, options };
      this.#startMap();
      return;
    }

    const marker = this.#markers.get(id);
    if (!marker) return;

    const reduceMotion = prefersReducedMotion();
    const focusZoom = Number(this.dataset.focusZoom) || 15;
    const zoom = Math.max(this.#map.getZoom(), focusZoom);

    this.#map.setView(marker.getLatLng(), zoom, { animate: !reduceMotion });
    marker.openPopup();

    const behavior = reduceMotion ? 'auto' : 'smooth';

    if (source === 'map') {
      item.scrollIntoView({ block: 'nearest', behavior });
    } else if (source === 'list' && !this.#isMapVisible()) {
      this.refs.map.scrollIntoView({ block: 'center', behavior });
    }
  }

  #startMap() {
    if (this.#initStarted) return;
    this.#initStarted = true;

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#initMap();
  }

  async #initMap() {
    const { leafletJs, leafletCss } = this.dataset;
    if (!leafletJs) return;

    let L;
    try {
      L = await loadLeaflet(leafletJs, leafletCss ?? '');
    } catch (error) {
      console.error(error);
      this.#showMapError();
      return;
    }

    // A komponens közben lekerülhetett az oldalról (pl. theme editor újrarenderelés).
    if (!this.isConnected || this.#map) return;

    this.#L = L;
    const mapElement = this.refs.map;
    mapElement.replaceChildren();

    const map = L.map(mapElement, {
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true,
    });
    this.#map = map;

    map.attributionControl.setPrefix(false);
    L.control
      .zoom({
        position: 'bottomright',
        zoomInTitle: this.dataset.zoomIn ?? '',
        zoomOutTitle: this.dataset.zoomOut ?? '',
      })
      .addTo(map);

    // Az oldal görgetését nem „lopja el”: görgős nagyítás csak a térképre kattintás/fókusz után.
    map.on('click focus', () => map.scrollWheelZoom.enable());
    map.on('blur mouseout', () => map.scrollWheelZoom.disable());

    const tileUrl = this.dataset.tileUrl;
    if (tileUrl) {
      L.tileLayer(tileUrl, {
        attribution: this.dataset.attribution ?? '',
        maxZoom: 19,
      }).addTo(map);
    }

    this.#markerLayer = L.layerGroup().addTo(map);
    this.#createMarkers();
    this.#fitToVisible(false);

    // A szekció mérete változhat (mobil/asztali váltás, téma beúszó animációja).
    this.#resizeObserver = new ResizeObserver(() => this.#map?.invalidateSize());
    this.#resizeObserver.observe(mapElement);

    if (this.#pendingSelection) {
      const { id, options } = this.#pendingSelection;
      this.#pendingSelection = null;
      this.selectStore(id, options);
    }
  }

  #createMarkers() {
    const L = this.#L;

    for (const item of this.refs.stores ?? []) {
      const id = item.dataset.storeId;
      const { lat, lng } = item.dataset;
      if (!id || !lat || !lng) continue;

      const latitude = Number(lat);
      const longitude = Number(lng);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;

      const name = item.dataset.storeName ?? '';
      const marker = L.marker([latitude, longitude], {
        icon: this.#createIcon(),
        title: name,
        alt: name,
        keyboard: true,
        riseOnHover: true,
      });

      marker.bindPopup(() => this.#createPopup(item), { autoPanPadding: [24, 24] });
      marker.on('click', () => this.selectStore(id, { source: 'map' }));

      this.#markers.set(id, marker);
      if (!item.hidden) this.#markerLayer.addLayer(marker);
    }
  }

  #createIcon() {
    const pin = document.createElement('span');
    pin.className = 'store-locator__pin';

    const logoUrl = this.dataset.markerLogo;
    if (logoUrl) {
      const image = document.createElement('img');
      image.src = logoUrl;
      image.alt = '';
      image.width = 28;
      image.height = 28;
      image.decoding = 'async';
      pin.append(image);
    }

    return this.#L.divIcon({
      className: 'store-locator__marker',
      html: pin,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -22],
    });
  }

  /**
   * A felugró ablak tartalma a listaelemből készül (textContent, így nincs HTML-befecskendezés).
   *
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #createPopup(item) {
    const wrapper = document.createElement('div');
    wrapper.className = 'store-locator__popup';

    const title = document.createElement('p');
    title.className = 'store-locator__popup-title';
    title.textContent = item.dataset.storeName ?? '';
    wrapper.append(title);

    if (item.dataset.storeAddress) {
      const address = document.createElement('p');
      address.textContent = item.dataset.storeAddress;
      wrapper.append(address);
    }

    const directions = item.querySelector('a[data-directions]');
    if (directions instanceof HTMLAnchorElement) {
      const link = /** @type {HTMLAnchorElement} */ (directions.cloneNode(true));
      link.className = 'store-locator__popup-link';
      link.removeAttribute('data-directions');
      wrapper.append(link);
    }

    return wrapper;
  }

  /**
   * @param {boolean} animate
   */
  #fitToVisible(animate) {
    const map = this.#map;
    const L = this.#L;
    if (!map || !L || !this.#markerLayer) return;

    /** @type {any[]} */
    const points = [];
    this.#markerLayer.eachLayer((/** @type {any} */ marker) => points.push(marker.getLatLng()));

    const shouldAnimate = animate && !prefersReducedMotion();
    const focusZoom = Number(this.dataset.focusZoom) || 15;

    if (points.length === 0) {
      map.setView(this.#defaultCenter(), Number(this.dataset.zoom) || 7, { animate: shouldAnimate });
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], focusZoom, { animate: shouldAnimate });
      return;
    }

    // Felül több hely kell, mert ott van a kereső.
    map.fitBounds(L.latLngBounds(points), {
      paddingTopLeft: [48, 110],
      paddingBottomRight: [48, 48],
      maxZoom: focusZoom,
      animate: shouldAnimate,
    });
  }

  /** @returns {[number, number]} */
  #defaultCenter() {
    const lat = Number(this.dataset.centerLat);
    const lng = Number(this.dataset.centerLng);
    return [lat || 47.1625, lng || 19.5033];
  }

  /**
   * @param {string} id
   */
  #setActive(id) {
    for (const item of this.refs.stores ?? []) {
      item.classList.toggle('is-active', item.dataset.storeId === id);
    }

    for (const [markerId, marker] of this.#markers) {
      const isActive = markerId === id;
      marker.getElement()?.classList.toggle('is-active', isActive);
      marker.setZIndexOffset(isActive ? 1000 : 0);
    }
  }

  /**
   * @param {string} id
   * @returns {HTMLElement | undefined}
   */
  #getItem(id) {
    return this.refs.stores?.find((item) => item.dataset.storeId === id);
  }

  /**
   * @param {number} count
   */
  #updateStatus(count) {
    const status = this.refs.status;
    if (!status) return;

    const template = (count === 1 ? this.dataset.resultsOne : this.dataset.resultsOther) ?? '';
    const text = template.replace('[count]', String(count));
    if (status.textContent !== text) status.textContent = text;
  }

  #isMapVisible() {
    const rect = this.refs.map.getBoundingClientRect();
    const margin = 80;
    return rect.bottom > margin && rect.top < window.innerHeight - margin;
  }

  #showMapError() {
    const message = document.createElement('p');
    message.className = 'store-locator__map-placeholder';
    message.textContent = this.dataset.mapError ?? '';
    this.refs.map.replaceChildren(message);
  }

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || detail.sectionId !== this.dataset.sectionId) return;
    this.selectStore(detail.blockId, { source: 'editor' });
  };
}

if (!customElements.get('store-locator-component')) {
  customElements.define('store-locator-component', StoreLocatorComponent);
}
